import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { RightPanel } from '../components/ui/right-panel';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, Users, Mail, Phone, Building2, Pencil, Trash2, X, User } from 'lucide-react';

import { PageShell, PageToolbar, SectionTabs, SECTIONS } from '../components/enterprise';

const EMPTY_CONTACT = { name: '', email: '', phone: '', function: '', department: '', role: '', notes: '', is_internal: true };

const ContactsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await api.get('/contacts');
      setContacts(res.data.contacts || []);
    } catch { toast.error('Kon contactpersonen niet laden'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const openCreate = () => { setEditContact(null); setForm(EMPTY_CONTACT); setDialogOpen(true); };
  const openEdit = (c) => { setEditContact(c); setForm({ ...c }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Naam is verplicht'); return; }
    setSaving(true);
    try {
      if (editContact) {
        await api.put(`/contacts/${editContact.id}`, form);
        toast.success('Contact bijgewerkt');
      } else {
        await api.post('/contacts', form);
        toast.success('Contact aangemaakt');
      }
      setDialogOpen(false);
      await fetchContacts();
    } catch { toast.error('Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      toast.success('Contact verwijderd');
      if (selected?.id === id) setSelected(null);
      await fetchContacts();
    } catch { toast.error('Verwijderen mislukt'); }
  };

  const filtered = contacts.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.department?.toLowerCase().includes(q) || c.function?.toLowerCase().includes(q);
  });

  if (loading) return <PageShell section="organisatie" testId="contacts-page" loading />;

  return (
    <PageShell section="organisatie" testId="contacts-page">
      <PageToolbar
        title="Contactpersonen"
        subtitle={`${contacts.length} contactpersonen geregistreerd`}
        count={contacts.length > 0 ? contacts.length : undefined}
        onAdd={openCreate}
        addLabel="Nieuw contact"
        testId="contacts"
      />

      <div className="px-6 py-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek op naam, e-mail, afdeling..." className="pl-9 h-10 rounded-lg" data-testid="contact-search" />
          </div>

          <div className="flex gap-6">
            <div className={`${selected ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
              {filtered.length === 0 ? (
                <div className="text-center py-16 text-neutral-400">
                  <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">{search ? 'Geen contactpersonen gevonden' : 'Nog geen contactpersonen'}</p>
                </div>
              ) : (
                filtered.map(c => (
                  <div key={c.id} onClick={() => setSelected(c)}
                    className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${selected?.id === c.id ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'}`}
                    data-testid={`contact-row-${c.id}`}>
                    <div className="h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center shrink-0">
                      <User className="h-5 w-5 text-neutral-400" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-black truncate">{c.name}</p>
                      <p className="text-[11px] text-neutral-500 truncate">{[c.function, c.department].filter(Boolean).join(', ') || 'Geen functie/afdeling'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={c.is_internal ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'} variant="outline">
                        {c.is_internal ? 'Intern' : 'Extern'}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>

            {selected && (
              <div className="w-1/2 sticky top-6 self-start">
                <Card className="border-neutral-200">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between p-4 border-b border-neutral-100">
                      <p className="text-sm font-semibold text-black">{selected.name}</p>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(selected)} data-testid="edit-contact-btn"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(selected.id)} data-testid="delete-contact-btn"><Trash2 className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSelected(null)}><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      {selected.email && <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-neutral-400" />{selected.email}</div>}
                      {selected.phone && <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-neutral-400" />{selected.phone}</div>}
                      {selected.department && <div className="flex items-center gap-2 text-sm"><Building2 className="h-4 w-4 text-neutral-400" />{selected.department}</div>}
                      {selected.function && <div className="flex items-center gap-2 text-sm"><User className="h-4 w-4 text-neutral-400" />{selected.function}</div>}
                      {selected.role && <div className="mt-3"><p className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider mb-1">Rol</p><p className="text-sm text-neutral-700">{selected.role}</p></div>}
                      {selected.notes && <div className="mt-3"><p className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider mb-1">Notities</p><p className="text-sm text-neutral-700">{selected.notes}</p></div>}
                      <p className="text-[10px] text-neutral-400 mt-3">Aangemaakt: {new Date(selected.created_at).toLocaleDateString('nl-NL')} door {selected.created_by}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="contact-form-dialog">
            <DialogHeader><DialogTitle>{editContact ? 'Contact bewerken' : 'Nieuw contact'}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-xs">Naam *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="contact-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">E-mail</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="contact-email" /></div>
                <div><Label className="text-xs">Telefoon</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Functie</Label><Input value={form.function} onChange={e => setForm(f => ({...f, function: e.target.value}))} /></div>
                <div><Label className="text-xs">Afdeling</Label><Input value={form.department} onChange={e => setForm(f => ({...f, department: e.target.value}))} /></div>
              </div>
              <div><Label className="text-xs">Rol in beveiliging</Label><Input value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} placeholder="Bijv. CISO, DPO, Risico-eigenaar..." /></div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.is_internal ? 'internal' : 'external'} onValueChange={v => setForm(f => ({...f, is_internal: v === 'internal'}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Intern (medewerker)</SelectItem>
                    <SelectItem value="external">Extern (leverancier/partner)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Notities</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-black text-white" data-testid="save-contact-btn">{editContact ? 'Opslaan' : 'Aanmaken'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageShell>
  );
};

export default ContactsPage;
